// ForgotPassword.js - Password reset with exact LoginForm.js branding
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.png';
import { SiteStyles } from '../site/SiteChrome';

// Step 1: Email Request Form
const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dark mode state with cookie persistence (matching LoginForm)
  const [darkModePref, setDarkMode] = useState(() => {
    const savedTheme = document.cookie
      .split('; ')
      .find(row => row.startsWith('theme='));
    
    if (savedTheme) {
      return savedTheme.split('=')[1] === 'dark';
    }
    
    const localTheme = localStorage.getItem('darkMode');
    if (localTheme) {
      return JSON.parse(localTheme);
    }
    
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const darkMode = true; // always the site's dark theme
  
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();

  // Apply theme to document and body (matching LoginForm)
  useEffect(() => {
    if (darkModePref) {
      document.documentElement.classList.add('dark');
      document.body.className = 'bg-[#07131F] min-h-screen transition-colors duration-300';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.className = 'bg-gray-50 min-h-screen transition-colors duration-300';
    }
    
    return () => {
      document.body.className = '';
    };
  }, [darkModePref]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('Password reset instructions have been sent to your email.');
      
      // Clear form after success
      setTimeout(() => {
        setEmail('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${
      darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-gray-200'
    } rounded-md border p-8`}>
      
      {/* Success Message (matching LoginForm style) */}
      {success && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <div>
              <p className="text-green-800 dark:text-green-200 font-medium">Email Sent Successfully!</p>
              <p className="text-green-700 dark:text-green-300 text-sm mt-1">{success}</p>
              <p className="text-green-600 dark:text-green-400 text-xs mt-2">
                Please check your inbox and follow the instructions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message (matching LoginForm style) */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <div>
              <p className="text-red-800 dark:text-red-200 font-medium">Request Failed</p>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Email Field (matching LoginForm style) */}
        <div className="space-y-1">
          <label className={`block text-sm font-medium ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              placeholder="Enter your registered email"
              className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                darkMode 
                  ? 'bg-[#13283C] border-[#28445F] text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading}
              autoComplete="email"
            />
          </div>
        </div>

        {/* Submit Button (matching LoginForm style) */}
        <button
          type="submit"
          disabled={loading || !email}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 ${
            loading || !email
              ? 'bg-[#1C3348] text-slate-500 cursor-not-allowed'
              : 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <span>Send Reset Instructions</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Back to Login Link (matching LoginForm style) */}
      <div className="text-center mt-6">
        <Link
          to="/login"
          className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
        >
          Back to Sign In
        </Link>
      </div>

      {/* Help Section (matching LoginForm style) */}
      <div className={`mt-8 p-4 rounded-lg ${
        darkMode ? 'bg-[#13283C]/30' : 'bg-blue-50/50'
      }`}>
        <div className="flex items-start space-x-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
            darkMode ? 'bg-blue-600' : 'bg-blue-500'
          }`}>
            <span className="text-white text-xs font-bold">?</span>
          </div>
          <div>
            <h3 className={`text-sm font-medium ${
              darkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Didn't receive the email?
            </h3>
            <p className={`text-xs mt-1 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Check your spam folder or try with a different email address. If you continue to have issues, contact support.
            </p>
            <a 
              href="mailto:support@tunasignal.ai"
              className={`inline-flex items-center space-x-1 text-xs mt-2 ${
                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
              }`}
            >
              <Mail className="w-3 h-3" />
              <span>support@tunasignal.ai</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 2: Reset Password Form
const ResetPasswordForm = ({ token }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({});
  
  // Dark mode state (matching LoginForm)
  const [darkModePref, setDarkMode] = useState(() => {
    const savedTheme = document.cookie
      .split('; ')
      .find(row => row.startsWith('theme='));
    
    if (savedTheme) {
      return savedTheme.split('=')[1] === 'dark';
    }
    
    const localTheme = localStorage.getItem('darkMode');
    if (localTheme) {
      return JSON.parse(localTheme);
    }
    
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const darkMode = true; // always the site's dark theme
  
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  // Apply theme (matching LoginForm)
  useEffect(() => {
    if (darkModePref) {
      document.documentElement.classList.add('dark');
      document.body.className = 'bg-[#07131F] min-h-screen transition-colors duration-300';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.className = 'bg-gray-50 min-h-screen transition-colors duration-300';
    }
    
    return () => {
      document.body.className = '';
    };
  }, [darkModePref]);

  const validatePassword = (password) => {
    const errors = {};
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain uppercase, lowercase, and number';
    }
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (error) setError('');
    
    if (name === 'password') {
      setValidation(validatePassword(value));
    } else if (name === 'confirmPassword') {
      if (value !== formData.password) {
        setValidation({ confirmPassword: 'Passwords do not match' });
      } else {
        setValidation({});
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const passwordErrors = validatePassword(formData.password);
    if (Object.keys(passwordErrors).length > 0) {
      setValidation(passwordErrors);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setValidation({ confirmPassword: 'Passwords do not match' });
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      await resetPassword(token, formData.password);
      setSuccess('Password reset successful! Redirecting to login...');
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Password reset successful! Please login with your new password.' 
          } 
        });
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={`${
        darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-gray-200'
      } rounded-md border p-8 text-center`}>
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Invalid Reset Link
        </h2>
        <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          This password reset link is invalid or has expired.
        </p>
        <Link
          to="/forgot-password"
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Request New Link</span>
        </Link>
      </div>
    );
  }

  return (
    <div className={`${
      darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-gray-200'
    } rounded-md border p-8`}>
      
      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <div>
              <p className="text-green-800 dark:text-green-200 font-medium">Success!</p>
              <p className="text-green-700 dark:text-green-300 text-sm mt-1">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <div>
              <p className="text-red-800 dark:text-red-200 font-medium">Reset Failed</p>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* New Password Field */}
        <div className="space-y-1">
          <label className={`block text-sm font-medium ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password"
              className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                validation.password
                  ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                  : darkMode 
                    ? 'bg-[#13283C] border-[#28445F] text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {validation.password && (
            <p className="text-red-600 dark:text-red-400 text-xs mt-1">
              {validation.password}
            </p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="space-y-1">
          <label className={`block text-sm font-medium ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Confirm New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                validation.confirmPassword
                  ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                  : darkMode 
                    ? 'bg-[#13283C] border-[#28445F] text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {validation.confirmPassword && (
            <p className="text-red-600 dark:text-red-400 text-xs mt-1">
              {validation.confirmPassword}
            </p>
          )}
        </div>

        {/* Password Requirements Info */}
        <div className={`p-3 rounded-lg text-xs space-y-1 ${
          darkMode ? 'bg-[#13283C]/50 text-gray-400' : 'bg-gray-50 text-gray-600'
        }`}>
          <p className="font-medium">Password must contain:</p>
          <ul className="space-y-0.5 ml-4">
            <li className={formData.password.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>
              • At least 8 characters
            </li>
            <li className={/[A-Z]/.test(formData.password) ? 'text-green-600 dark:text-green-400' : ''}>
              • One uppercase letter
            </li>
            <li className={/[a-z]/.test(formData.password) ? 'text-green-600 dark:text-green-400' : ''}>
              • One lowercase letter
            </li>
            <li className={/\d/.test(formData.password) ? 'text-green-600 dark:text-green-400' : ''}>
              • One number
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !formData.password || !formData.confirmPassword}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 ${
            loading || !formData.password || !formData.confirmPassword
              ? 'bg-[#1C3348] text-slate-500 cursor-not-allowed'
              : 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Resetting...</span>
            </>
          ) : (
            <>
              <span>Reset Password</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Back to Login Link */}
      <div className="text-center mt-6">
        <Link
          to="/login"
          className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
        >
          Back to Sign In
        </Link>
      </div>
    </div>
  );
};

// Main Component Wrapper (matching LoginPage structure)
const ForgotPassword = () => {
  const { token } = useParams();
  const [darkModePref, setDarkMode] = useState(() => {
    const savedTheme = document.cookie
      .split('; ')
      .find(row => row.startsWith('theme='));
    
    if (savedTheme) {
      return savedTheme.split('=')[1] === 'dark';
    }
    
    const localTheme = localStorage.getItem('darkMode');
    if (localTheme) {
      return JSON.parse(localTheme);
    }
    
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const darkMode = true; // always the site's dark theme

  useEffect(() => {
    if (darkMode) {
      document.body.className = 'bg-[#07131F] min-h-screen';
    } else {
      document.body.className = 'bg-gray-50 min-h-screen';
    }
    
    const handleStorageChange = () => {
      const newTheme = localStorage.getItem('darkMode');
      if (newTheme) {
        setDarkMode(JSON.parse(newTheme));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.body.className = '';
    };
  }, [darkModePref]);

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-[#07131F]' : 'bg-gray-50'
    } flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        {/* Header with Logo (matching LoginPage) */}
        <div className="text-center relative">
          <div className="flex justify-center">
            <div className="w-16 h-16 flex items-center justify-center">
              <img src={tunaLogo} alt="TunaSignal AI" className="w-16 h-16" />
            </div>
          </div>
          <h1 className={`mt-4 text-3xl font-bold ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            TunaSignal AI
          </h1>
          <p className={`mt-2 ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {token ? 'Reset your password' : 'Recover your account'}
          </p>
        </div>

        {/* Show appropriate form */}
        {token ? (
          <ResetPasswordForm token={token} />
        ) : (
          <ForgotPasswordForm />
        )}
      </div>
    </div>
    </div>
  );
};

export default ForgotPassword;